import { Injectable } from '@angular/core';
import { Observable } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { NavigationExtras, Router } from "@angular/router";
import { RequestMethod } from 'src/app/classes/RequestMethod';
import { ToastrService } from 'ngx-toastr';
import { ToastType } from 'src/app/classes/ToastType';
import { Config } from 'src/app/classes/Config';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { HttpClickHandler } from 'src/app/directives/http-click.directive';
import { Right } from 'src/app/classes/Right';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslatePipe } from '../shared/translate/translate.pipe';
import { timer } from 'rxjs';
import { SortObject } from '../classes/SortObject';
import { SortDirection } from '../classes/SortDirection';
import { MatSort } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import { User } from '../classes/User';
import jwt_decode from "jwt-decode";
import { FilterablePageRequest } from '../classes/FilterablePageRequest';
import { SearchCriterion } from '../classes/SearchCriterion';
import { FilterOperator } from '../classes/FilterOperator';

@Injectable({
  providedIn: 'root'
})
export class Util {

  private serviceUrl: string;
  private serviceUrl2: string;
  public timezoneOffset: string = String(new Date().getTimezoneOffset());
  private readonly datePipe: DatePipe = new DatePipe("en-GB");

  constructor(private httpClient: HttpClient, private router: Router, private toastr: ToastrService,
    private translatePipe: TranslatePipe, private dialog: MatDialog, private translationService: TranslationService) {
    this.serviceUrl = Config.BACK_END_URL + "/api/";
    this.serviceUrl2 = Config.BACK_END_URL2 + "/api/";
  }

  /**
   * 
   * @param url URL of API
   * @param data payload to send
   * @param httpClickHandler whether you want to enable/disable the button during the request (pass the $event from the HTML)
   * @param requestMethod default is POST when not presnet, send other types if needed
   * @param customHttpOptions an object which may contain 'headers' key with HttpHeaders as value
   */
  createApi(url: string, data?: any, httpClickHandler?: HttpClickHandler, requestMethod?: RequestMethod, customHttpOptions?: object): Observable<any> {
    url = this.serviceUrl.concat(url);
    const httpOptions = this.generateTokenHttpOptions(url, requestMethod);
    if (requestMethod && requestMethod === RequestMethod.GET) {
      return this.httpClient.get(url, httpOptions).pipe(finalize(() => httpClickHandler ? httpClickHandler.enable() : null));
    } else {
      // let httpOptions = customHttpOptions;
      // if (!customHttpOptions) {//default
      //   httpOptions = {
      //     headers: new HttpHeaders({
      //       'Content-type': 'application/json'
      //     })
      //   };
      // }
      return this.httpClient.post(url, data, httpOptions)
        .pipe(finalize(() => httpClickHandler ? httpClickHandler.enable() : null));
    }
  }

  createSsoApi(url: string, data?: any, httpClickHandler?: HttpClickHandler, requestMethod?: RequestMethod, customHttpOptions?: object): Observable<any> {
    url = this.serviceUrl2.concat(url);
    if (requestMethod && requestMethod === RequestMethod.GET) {
      return this.httpClient.get(url).pipe(finalize(() => httpClickHandler ? httpClickHandler.enable() : null));
    } else {
      let httpOptions = customHttpOptions;
      if (!customHttpOptions) {//default
        httpOptions = {
          headers: new HttpHeaders({
            'Content-type': 'application/json'
          })
        };
      }
      return this.httpClient.post(url, data, httpOptions)
        .pipe(finalize(() => httpClickHandler ? httpClickHandler.enable() : null));
    }
  }

  generateTokenHttpOptions(url: string, requestMethod?: RequestMethod): any {
    let options = {
      headers: new HttpHeaders(),
    };

    if (!requestMethod || requestMethod == RequestMethod.POST) {
      options.headers = options.headers.set('Content-type', 'application/json');
    }

    if (!url.toLowerCase().includes("login")) {
      const token = this.getFromStorage("token");
      if (token) {
        options.headers = options.headers.set('Authorization', 'Bearer ' + token);
      }
    }


    return options;
  }

  navigate(path: string, extras?: NavigationExtras): void {
    this.router.navigate([path], extras);
  }

  getRoutes(): any[] {
    let routes = this.router.config;
    routes.sort((a, b) => {
      if (!a.data || !b.data) {
        return 0;
      }
      if (a.data.order > b.data.order) {
        return 1;
      }
      if (a.data.order < b.data.order) {
        return -1;
      }
      return 0;
    });
    routes = routes.filter(r => r.data);
    return routes;
  }

  createToast(message: string, toastType: ToastType, titleCode?: string, onClick?: Function, configOverride?: any) {
    let title: string;
    if (!titleCode) {
      //dynamic title depending on type
      switch (toastType) {
        case ToastType.SUCCESS:
          title = this.translatePipe.getLabel("successTitle");
          break;
        case ToastType.ERROR:
          title = this.translatePipe.getLabel("error");
          break;
        case ToastType.WARNING:
          title = this.translatePipe.getLabel("warning");
          break;
        case ToastType.INFO:
          title = this.translatePipe.getLabel("info");
          break;
      }
    } else {
      //custom title
      title = this.translatePipe.getLabel(titleCode);
    }
    //toast config overrides
    let configOverrides = { positionClass: "toast-top-right" };

    if (this.isMobile()) {
      configOverrides.positionClass = "toast-bottom-full-width";
    } else if (this.translationService.getDirection() === "rtl") {
      configOverrides.positionClass = "toast-top-left";
    }

    if (configOverride) {
      for (var key in configOverride) {
        if (Object.prototype.hasOwnProperty.call(configOverride, key)) {
          configOverrides[key] = configOverride[key];
        }
      }
    }

    this.toastr.show(message, title, configOverrides, toastType).onTap.subscribe(data => {
      if (onClick) {
        onClick();
      }
    });
  }

  getDeepValue(obj: any, path: string): any {
    let keys = path.split('.');
    let length = keys.length;
    for (let i = 0; i < length; i++) {
      if (obj == null) {
        return null;
      }
      obj = obj[keys[i]];
    };
    return obj;
  }

  openDialog(component: any, config: MatDialogConfig): MatDialogRef<any> {
    //global configs
    config.direction = this.translationService.getDirection() === "ltr" ? "ltr" : "rtl";
    return this.dialog.open(component, config);
  }

  getErrorMessage(formControl: AbstractControl) {
    if (!formControl.errors) {
      return "";
    }
    let messages = [];
    if (formControl.hasError('noShowErrors')) {
      messages.push("");
    }
    if (formControl.hasError('required')) {
      messages.push(this.translatePipe.getLabel("fieldRequired"));
    }
    if (formControl.hasError('matchPassword')) {
      messages.push(this.translatePipe.getLabel("passwordNoMatch"));
    }
    if (formControl.hasError('email') || formControl.hasError('pattern') || formControl.hasError('mask')) {
      messages.push(this.translatePipe.getLabel("invalidFormat"));
    }
    if (formControl.hasError('min')) {
      let minError = formControl.getError('min');
      messages.push(this.translatePipe.getLabel("lessThanMinimum") + ": " + minError.min);
    }
    if (formControl.hasError('max')) {
      let maxError = formControl.getError('max');
      messages.push(this.translatePipe.getLabel("moreThanMaximum") + ": " + maxError.max);
    }
    if (formControl.hasError('minlength')) {
      let minLengthError = formControl.getError('minlength');
      messages.push(this.translatePipe.getLabel("lessThanMinimum") + ": " + minLengthError.requiredLength);
    }
    if (formControl.hasError('maxlength')) {
      let maxLengthError = formControl.getError('maxlength');
      messages.push(this.translatePipe.getLabel("moreThanMaximum") + ": " + maxLengthError.requiredLength);
    }
    if (formControl.hasError('lowerThan')) {
      let error = formControl.getError('lowerThan');
      messages.push(this.translatePipe.getLabel("lessThanMinimum") + ": " + error.max);
    }
    if (formControl.hasError('moreThan')) {
      let error = formControl.getError('moreThan');
      messages.push(this.translatePipe.getLabel("moreThanMaximum") + ": " + error.min);
    }
    if (formControl.hasError('matDatepickerMax') || formControl.hasError('matDatepickerMin') ||
      formControl.hasError('matDatepickerParse') || formControl.hasError('invalidDate')) {
      //matDatepickerMax only fires when we type invalid date like month = 13
      //matDatepickerParse for min max
      messages.push(this.translatePipe.getLabel("invalidDate"));
    }

    if (messages.length > 1) {
      messages = messages.filter(obj => obj !== "");
    }

    return messages.length > 0 ? messages.join(" | ") : this.translatePipe.getLabel("error");
  }

  setToStorage(storageKey: string, object: any, isLocalStorage: boolean) {
    let value = object;
    if (typeof object !== "string") {
      value = JSON.stringify(object);
    }
    if (isLocalStorage) {
      localStorage.setItem(storageKey, value);
    } else {
      sessionStorage.setItem(storageKey, value);
    }
  }

  getFromStorage<T>(storageKey: string): T {
    let storageObj: any = localStorage.getItem(storageKey);
    if (!storageObj) {
      storageObj = sessionStorage.getItem(storageKey);
    }
    try {//try to parse if its an object
      storageObj = JSON.parse(storageObj);
    } catch { }
    //even if storageObj the casting will work and will return null 
    return storageObj as T;
  }

  clearStorage(clearLocal: boolean, clearSession: boolean): void {
    if (clearLocal) {
      localStorage.clear();
    }
    if (clearSession) {
      sessionStorage.clear();
    }
  }

  clearItemFromStorage(storageKey: string, isLocalStorage: boolean, isSessionStorage: boolean): void {
    if (isLocalStorage) {
      localStorage.removeItem(storageKey);
    }
    if (isSessionStorage) {
      sessionStorage.removeItem(storageKey);
    }
  }

  trimTime(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  addDays(date: Date, amount: number) {
    let day = date.getDate() + amount;
    return new Date(date.getFullYear(), date.getMonth(), day, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  }

  addMinutes(date: Date, amount: number) {
    let minutes = date.getMinutes() + amount;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), minutes, date.getSeconds(), date.getMilliseconds());
  }

  addSeconds(date: Date, amount: number) {
    let seconds = date.getSeconds() + amount;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), seconds, date.getMilliseconds());
  }

  dateToSend(date: Date) {
    const year = date.getFullYear();
    let month: string = (date.getMonth() + 1).toString();
    if (month.length === 1) {
      month = '0' + month;
    }
    let day: string = date.getDate().toString();
    if (day.length === 1) {
      day = '0' + day;
    }
    return `${day}/${month}/${year}`;
  }

  isStringEmpty(value: string): boolean {
    return !value || value.trim().length === 0;
  }

  copy<T>(object: T): T {
    return JSON.parse(JSON.stringify(object));
  }

  public hasRight(right: Right, user: User): boolean {
    return user.Privileges[right];
  }

  public doIfhasRight(right: Right, user: User, callback: Function): void {
    if (this.hasRight(right, user)) {
      callback();
    }
  }

  isMobile(): boolean {
    //todo:add cordova api
    var ua = navigator.userAgent;
    return (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua));
  }

  insertTimezoneKeys(obj: any) {
    let currentDate = new Date();
    obj["timezoneOffset"] = currentDate.getTimezoneOffset();
    obj["timezoneId"] = currentDate.toString().split('(')[1].slice(0, -1).replace('Daylight', 'Standard');
  }

  validateFileName(fileName: string) {
    return fileName.replace(/[\\?*:\";|/<>]+$/g, "_");
  }

  handleFile(data: any, fileName: string, type: string, extension: string) {
    var blob = data;
    if (!(data instanceof Blob)) {
      blob = new Blob([data], { type: type });
    }
    var url = window.URL.createObjectURL(blob);

    var link = document.createElement('a');
    link.download = fileName + extension;
    link.href = url;
    link.target = "_blank";
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }

  openLink(url: string) {
    window.open(url, '_blank');
  }

  passwordStrengthCheck(password: string) {
    var strengthMeter = {
      score: 0,
      strength: "weak",
      digits: false,
      lower: false,
      upper: false,
      special: false,
      length: 0
    };
    if (!password) {
      return strengthMeter;
    }
    strengthMeter.length = password.length;

    var letters = new Object();
    // award every unique letter until 5 repetitions
    for (var i = 0; i < password.length; i++) {
      letters[password[i]] = (letters[password[i]] || 0) + 1;
      strengthMeter.score += 5.0 / letters[password[i]];
    }

    // bonus points for mixing it up
    var variations = {
      digits: /\d/.test(password),
      lower: /[a-z]/.test(password),
      upper: /[A-Z]/.test(password),
      special: /[!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/.test(password),
    }

    var variationCount = 0;
    for (var check in variations) {
      var result = variations[check]
      variationCount += result ? 1 : 0;
      strengthMeter[check] = result;
    }
    strengthMeter.score += (variationCount - 1) * 10;
    if (strengthMeter.score > 80) {
      strengthMeter.strength = "strong";
    } else if (strengthMeter.score > 60) {
      strengthMeter.strength = "good";
    } else {
      strengthMeter.strength = "weak";
    }
    return strengthMeter;
  }

  timer(counterData: any, deadlineDate: Date, finishCallback: Function) {
    let currentDate = new Date();
    currentDate.setMilliseconds(0);
    let remainingTime = Math.floor((deadlineDate.getTime() - currentDate.getTime()) / 1000);
    let subscription = timer(0, 1000).subscribe(() => {
      --remainingTime;
      let value = remainingTime;
      if (value < 0) {
        subscription.unsubscribe();
        finishCallback();
        return;
      }
      counterData.days = Math.floor(value / 86400);

      value %= 86400;
      counterData.hours = ('00' + Math.floor(value / 3600)).slice(-2);

      value %= 3600;
      counterData.minutes = ('00' + Math.floor(value / 60)).slice(-2);

      value %= 60
      counterData.seconds = ('00' + Math.floor(value)).slice(-2);
    });
  }

  matchPasswordValidatior: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('newPassword');
    const confirmPassword = control.get('confirmNewPassword');
    return password?.value === confirmPassword?.value ? null : { notmatched: true };
  };


  numValidator(min, max): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let value = control.value;
      if (/[٠-٩]/.test(value)) {
        value = this.arabicNumToEnglishNum(value);
      }
      let parsedValue = parseInt(value);
      if (parsedValue < min) {
        return { "min": { value: control.value, min: min } }
      } else if (value > max) {
        return { "max": { value: control.value, max: max } }
      }
      return null;
    };
  }

  arabicNumToEnglishNum(arabicNumStr: string) {
    var str = "";
    for (var i = 0; i < arabicNumStr.length; i++) {
      var c = arabicNumStr.charCodeAt(i);
      if (c > 1631 && c < 1642) { //between ٠ and ٩ (arabic numerals)
        c = c - 1584; //shift them to english numerals
      }
      str += String.fromCharCode(c);
    }
    return str;
  }

  getSortFromDataSource(matSort: MatSort, keyReplaceMap?: any): Array<SortObject> {
    /**
     * return an array since angular sort only outputs one sort object
     */
    let sorts: SortObject[] = [];
    if (matSort && matSort.active && matSort.sortables.get(matSort.active) && matSort.direction) {
      sorts = [new SortObject(matSort.active, SortDirection[matSort.direction.toUpperCase()])];
      if (keyReplaceMap) {
        for (let key in keyReplaceMap) {
          let sort = sorts.find(e => e.property === key);
          if (sort) {
            sort.property = keyReplaceMap[key];
          }
        }
      }
    }
    return sorts;
  }

  generateBasicDoBFormat(dayControl: AbstractControl, monthControl: AbstractControl, yearControl: AbstractControl): string {
    if (dayControl.invalid || monthControl.invalid || yearControl.invalid ||
      !dayControl.value || !monthControl.value || !yearControl.value) {
      return null;
    }
    let day = parseInt(this.arabicNumToEnglishNum(dayControl.value));
    let month = parseInt(this.arabicNumToEnglishNum(monthControl.value));
    let year = parseInt(this.arabicNumToEnglishNum(yearControl.value));
    if (day == null || month == null || year == null) {
      return null;
    }
    return day + "/" + month + "/" + year;
  }

  formatDate(date: Date, format: string) {
    return this.datePipe.transform(date, format);
  }

  /**
   * Return the date in UTC with the same values. Ex: Sun Jan 03 2021 14:00:00 GMT+0200 will become Sun Jan 03 2021 14:00:00 GMT
   * @param date The date object in your local timezone
   */
  convertDateToUTC(date: Date) {
    date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
    return date;
  }

  /**
   * 
   * @param from 
   * @param to : nullable
   * @returns regex contains from to with english digits and arabic digits
   */
  generateEnArReg(from: number, to?: number): string {
    let length = to != null ? `{${from},${to}}` : `{${from}}`;
    return `(\\d|[٠-٩])${length}`;
  }

  decodeToken(token: string) {
    return jwt_decode(token);
  }

  /**
   * @param searchForm : search form name
   * @param textSearchFieldsList : list of text search fields from searchForm
   * @returns FilterablePageRequest object
  */
  getSearchFilters(searchForm: any, textSearchFieldsList: any[]): FilterablePageRequest {
    let wrapper = new FilterablePageRequest();
    let textSearchFields = textSearchFieldsList;
    if (searchForm.valid) {
      let formValue = searchForm.value;

      for (let field of textSearchFields) {
        //Handle the boolean value inside the component method
        if (typeof formValue[field] === "boolean") {
          continue;
          //   wrapper.filters.push(new SearchCriterion(field, formValue[field], FilterOperator.eq))
        }
        if (formValue[field] != null && formValue[field] != "") {
          wrapper.filters.push(new SearchCriterion(field, formValue[field], FilterOperator.contains));
        }
      }
    }

    return wrapper;
  }

  getKeyByValue(enumObject: any, value: number): string | undefined {
    return Object.keys(enumObject).find((key) => enumObject[key] === value);
  }
}
